import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  Dispatch,
  SetStateAction,
} from "react";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { UrlInput } from "@remo-co/ui-core/src/components/UrlInput";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { getGooglePicBySize, isGoogleUserImage } from "helpers/profileHelper";
import { deleteFile } from "services/firebaseService/files";
import logger from "logging/logger";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useI18n } from "i18n";
import { IAfterFileUpload } from "services/uploadService/uploadService";
import { IUserProfile } from "modules/app/types";
import useUserProfile from "../../userProfile/hooks/useUserProfile";
import useNotificationActions from "../../notification/hooks/useNotificationActions";
import useAuth from "../../auth/hooks/useAuth";
import { useStyles } from "./styles";
import { additionalFormInputs, defaultFormInputs } from "./constants";
import ProfileImageEditor from "../../userProfileForm/ProfileImageEditor";

interface Props {
  goToNextStep?: () => void;
  showAdditionalFields: boolean;
  setShowAdditionalFields: Dispatch<SetStateAction<boolean>>;
}

const CardForm = ({
  goToNextStep,
  showAdditionalFields,
  setShowAdditionalFields,
}: Props): JSX.Element => {
  const { t } = useI18n(["common", "onboarding", "server"]);
  const [profileData, setProfileData] = useState<IUserProfile>({
    firstName: "",
    lastName: "",
    title: "",
    company: "",
    headline: "",
    picture: "default",
    meetingLink: "",
    linkedInProfileURL: "",
    sansanProfileURL: "",
    facebookProfileURL: "",
    website: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { getMyProfile, updateMyProfile } = useUserProfile();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const defaultProfileImage =
    profileData && profileData.picture
      ? getGooglePicBySize(profileData.picture, 500)
      : "default";
  const [profileImage, setProfileImage] = useState(defaultProfileImage);
  const pictureUrls = useRef([profileImage]);
  const updatedUrl = useRef<string>();
  const { setAuthUser } = useAuth();
  const { track } = useContext(TRACKING_CONTEXT);
  const styles = useStyles();

  useEffect(() => {
    const getUser = async () => {
      try {
        const user = await getMyProfile();
        const userProfile = user.profile ? user.profile : undefined;

        if (userProfile) {
          setProfileData({ ...userProfile });
        }

        if (userProfile?.picture) {
          setProfileImage(getGooglePicBySize(userProfile.picture, 500));

          updatedUrl.current = userProfile.picture;
          pictureUrls.current = [userProfile.picture];
        }
      } catch (error) {
        logger.error("[CardForm] An error occurred loading profile", {
          err: error,
        });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    getUser();

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return onDestroy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDestroy = () => {
    if (pictureUrls.current) {
      pictureUrls.current.forEach((url: string) => {
        if (url !== updatedUrl.current && url !== "default") {
          try {
            deleteFile(url);
          } catch (e) {
            logger.error(
              `[ProfilePage][onDestroy] Not able to delete file.${e}`,
            );
          }
        }
      });
    }
  };

  const afterImageUpload: IAfterFileUpload<unknown> = (
    err,
    origin,
    url,
    file,
    // eslint-disable-next-line max-params
  ) => {
    if (err) {
      addErrorNotification({
        message: t(err.message),
        position: "tc",
        autoDismiss: 5,
      });

      return;
    }

    if (file) {
      if (!url) {
        throw new Error(`Url not formed for file ${file.name}`);
      }
      setIsLoading(false);
      setProfileImage(getGooglePicBySize(url, 500));
      pictureUrls.current.push(url);
      track(Events.ATTENDEE_ONBOARDING_PROFILE_IMAGE_UPLOADED);
    }
  };

  // eslint-disable-next-line consistent-return
  const saveChanges = async () => {
    if (!profileData.firstName && !profileData.lastName) {
      addErrorNotification({
        message: t("onboarding:fill.name.to.save.changes"),
        position: "tc",
        autoDismiss: 5,
      });

      return false;
    }

    setIsLoading(true);
    const newProfileImage = isGoogleUserImage(profileImage)
      ? profileData.picture
      : profileImage || "default";

    try {
      const userProfile = await updateMyProfile({
        ...profileData,
        picture: newProfileImage,
      });

      setProfileImage(
        getGooglePicBySize(userProfile.profile.picture ?? "", 500),
      );
      updatedUrl.current = userProfile.profile.picture;
      setAuthUser(userProfile);
      addSuccessNotification({
        message: t("onboarding:profile.updated.success"),
        position: "tc",
        autoDismiss: 5,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : JSON.stringify(err);

      logger.error(
        `[EditProfileContainer][saveChanges] Error saving profile: ${errorMessage}`,
        err instanceof Error ? err : new Error(errorMessage),
      );
      addErrorNotification({
        message: t(errorMessage),
        position: "tc",
        autoDismiss: 5,
      });
    }
    setIsLoading(false);

    goToNextStep?.();
  };

  const handleAttributeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProfileData({ ...profileData, [event.target.name]: event.target.value });
  };

  const showAdditionalFieldsCallback = useCallback(() => {
    setShowAdditionalFields(true);
  }, [setShowAdditionalFields]);

  return (
    <div className={styles.cardFormContainer}>
      {isLoading && (
        <PageLoader fullScreen message={`${t("please.wait")}...`} />
      )}
      {profileData && (
        <>
          <div>
            <ProfileImageEditor
              size={65}
              profileImage={profileImage}
              afterImageUpload={afterImageUpload}
              onLoading={setIsLoading}
              setProfileImage={setProfileImage}
              noImageLabel={t("onboarding:add.profile.picture")}
              hideRemoveBtn
              isLoading={isLoading}
            />
          </div>
          <div className={styles.inputContainer}>
            {defaultFormInputs.map((formInput) => (
              <Container key={formInput.name} bottom="sm">
                <Input
                  name={formInput.name}
                  id={formInput.id}
                  label={t(formInput.label)}
                  required={formInput.required}
                  fullWidth
                  value={profileData[formInput.name] ?? ""}
                  onChange={handleAttributeChange}
                  inputProps={{
                    maxLength: formInput.maxLength,
                    fontSize: "20px",
                    fontWeight: 500,
                    "data-testid": formInput.name,
                  }}
                  className="text"
                />
              </Container>
            ))}
          </div>
          {showAdditionalFields &&
            additionalFormInputs.map((formInput, index) =>
              formInput.isUrl ? (
                <Box mb={2} key={formInput.name}>
                  <UrlInput
                    key={formInput.name}
                    name={formInput.name}
                    label={t(formInput.label)}
                    fullWidth
                    value={profileData[formInput.name] ?? ""}
                    onChange={handleAttributeChange}
                    inputProps={{ fontSize: "20px", fontWeight: 500 }}
                    className="text"
                    errorMessage={t("incorrect.url.message")}
                  />
                </Box>
              ) : (
                <Container key={formInput.name} bottom="sm">
                  <Input
                    autoFocus={index === 0}
                    name={formInput.name}
                    label={t(formInput.label)}
                    fullWidth
                    value={profileData[formInput.name] ?? ""}
                    onChange={handleAttributeChange}
                    inputProps={{
                      fontSize: "20px",
                      fontWeight: 500,
                      "data-testid": formInput.label,
                    }}
                    className="text"
                  />
                </Container>
              ),
            )}
          {!showAdditionalFields && (
            <Button
              variant="text"
              type="button"
              className={styles.textLinkBtn}
              onClick={showAdditionalFieldsCallback}
            >
              <Typography variant="h5" className={styles.textLinkText}>
                + {t("onboarding:add.more.information")}
              </Typography>
            </Button>
          )}
          <div className="onboarding-form-footer">
            <Button
              variant="primary"
              className={styles.nextButton}
              onClick={saveChanges}
              data-testid="save-and-join"
            >
              {t("onboarding:save.profile.and.join.event")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CardForm;
